import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Typography,
  Modal,
  Input,
  InputNumber,
  message,
  Card,
  Tag,
  Row,
  Col,
  Divider,
  Table,
  Space,
  DatePicker,
  Checkbox,
  Select,
  Tabs,
  Switch,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BASE_API_URL } from "../../../utils/consts";
import { isLocationActive } from "../../../utils/helpers/locationVisibility";
import dayjs, { Dayjs } from "dayjs";
import "./BusinessCategories.scss";
import ExtraIncomeTypesTab from "./ExtraIncomeTypesTab";

const { confirm } = Modal;
const { Option } = Select;

// =======================
//        TYPES
// =======================
interface Supplier {
  _id: string;
  name: string;
  defaultExpense?: number;
  defaultStartsAt?: string;
  defaultExpenseHistory?: {
    value: number;
    startsAt: string;
    locationId?: string | null;
  }[];
  isFrozen?: boolean;
  frozenAt?: string;
  frozenBy?: string;
  frozenReason?: string;
}

interface SubType {
  _id: string;
  name: string;
}

export interface ExpenseCategory {
  _id: string;
  clientId: string;
  name: string;
  expenseType?: "operating" | "direct";
  isFrozen?: boolean;
  frozenAt?: string;
  frozenBy?: string;
  frozenReason?: string;
  suppliers: Supplier[];
  subTypes: SubType[];
  createdAt?: string;
  updatedAt?: string;
}

// =======================
//       COMPONENT
// =======================
const BusinessCategoriesSettings: React.FC = () => {
  const userData = useSelector((state: any) => state.userData.user);
  const { t } = useTranslation();

  const [categories, setCategories] = useState<any>(undefined);
  const [selectedCategory, setSelectedCategory] =
    useState<ExpenseCategory | null>(null);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isSupplierModalOpen, setSupplierModalOpen] = useState(false);
  const [isSubTypeModalOpen, setSubTypeModalOpen] = useState(false);
  const [filterTerm, setFilterTerm] = useState("");
  const [showFrozen, setShowFrozen] = useState(false);

  const [newCategoryName, setNewCategoryName] = useState("");
  const [newSupplierName, setNewSupplierName] = useState("");
  const [newSupplierDefault, setNewSupplierDefault] = useState<number | null>(
    0,
  );
  const [newSupplierStartsAt, setNewSupplierStartsAt] = useState<Dayjs | null>(
    dayjs(),
  );
  const [newSubTypeName, setNewSubTypeName] = useState("");

  const [editingCategory, setEditingCategory] =
    useState<ExpenseCategory | null>(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editCategoryExpenseType, setEditCategoryExpenseType] = useState<
    "operating" | "direct"
  >("operating");

  const [editingSupplier, setEditingSupplier] = useState<{
    categoryId: string;
    supplierId: string;
    name: string;
    defaultExpense?: number;
    defaultStartsAt?: Dayjs | null;
    excludeFromBusiness?: boolean;
    currentDefault?: number;
  } | null>(null);
  const [supplierHistory, setSupplierHistory] = useState<
    { value: number; startsAt: string; locationId?: string | null }[]
  >([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [editingHistory, setEditingHistory] = useState<{
    id: any;
    value: number;
    startsAt: Dayjs | null;
    locationId: string | null;
  } | null>(null);
  const [editingSubType, setEditingSubType] = useState<{
    categoryId: string;
    subTypeId: string;
    name: string;
  } | null>(null);
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [supplierSort, setSupplierSort] = useState<"asc" | "desc">("asc");
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedDefaultLocation, setSelectedDefaultLocation] = useState<
    string | null
  >("global");
  const [editingDefaultLocation, setEditingDefaultLocation] = useState<
    string | null
  >("global");
  const locationMap = useMemo<Record<string, string>>(
    () =>
      (locations || []).reduce((acc: Record<string, string>, loc: any) => {
        if (loc?._id) acc[loc._id] = loc.name;
        return acc;
      }, {}),
    [locations],
  );

  const filteredCategories = (categories || []).filter((cat: ExpenseCategory) =>
    cat.name.toLowerCase().includes(filterTerm.trim().toLowerCase()),
  );

  const sortSuppliers = (suppliers: Supplier[]) =>
    [...suppliers].sort((a, b) => {
      const cmp = a.name.localeCompare(b.name, "sr", {
        sensitivity: "base",
        numeric: true,
      });
      return supplierSort === "asc" ? cmp : -cmp;
    });

  const handleSupplierClick = (cat: ExpenseCategory, sup: Supplier) => {
    if (sup.isFrozen) {
      confirm({
        title: t("Unfreeze Supplier?"),
        content: t("The supplier will be available again."),
        okText: t("Unfreeze"),
        onOk: () => deleteSupplierSafe(cat._id, sup._id, true),
      });
    } else {
      openEditSupplier(cat._id, sup._id);
    }
  };

  const handleSupplierFreezeToggle = (
    e: React.MouseEvent,
    cat: ExpenseCategory,
    sup: Supplier,
  ) => {
    e.stopPropagation();
    confirm({
      title: sup.isFrozen ? t("Unfreeze Supplier?") : t("Freeze Supplier?"),
      content: sup.isFrozen
        ? t("The supplier will be available again.")
        : t("The supplier will not be visible in lists, but data remains saved."),
      okType: sup.isFrozen ? "default" : "danger",
      okText: sup.isFrozen ? t("Unfreeze") : t("Freeze"),
      onOk: () => deleteSupplierSafe(cat._id, sup._id, sup.isFrozen),
    });
  };

  const renderSupplierList = (cat: ExpenseCategory) => (
    <div className="bc-supplier-grid">
      {sortSuppliers(cat.suppliers).map((sup) => (
        <div
          key={sup._id}
          className={`bc-supplier-item ${sup.isFrozen ? "bc-supplier-item--frozen" : ""}`}
          onClick={() => handleSupplierClick(cat, sup)}
        >
          <Tooltip title={sup.name} mouseEnterDelay={0.3}>
            <span className="bc-supplier-item__name">{sup.name}</span>
          </Tooltip>
          <span className="bc-supplier-item__actions">
            {!sup.isFrozen && (
              <button
                type="button"
                className="bc-supplier-item__icon-btn"
                title={t("Edit")}
                onClick={(e) => {
                  e.stopPropagation();
                  openEditSupplier(cat._id, sup._id);
                }}
              >
                <EditOutlined />
              </button>
            )}
            <button
              type="button"
              className="bc-supplier-item__icon-btn"
              title={sup.isFrozen ? t("Unfreeze") : t("Freeze")}
              onClick={(e) => handleSupplierFreezeToggle(e, cat, sup)}
            >
              {sup.isFrozen ? <UnlockOutlined /> : <LockOutlined />}
            </button>
          </span>
        </div>
      ))}
    </div>
  );

  const tableColumns = [
    {
      title: "Kategorija",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <Typography.Text strong>{name}</Typography.Text>
      ),
    },
    {
      title: t("Expense Type"),
      key: "expenseType",
      render: (_: any, row: ExpenseCategory) => (
        <Tag color={row.expenseType === "operating" ? "blue" : "green"}>
          {t(
            row.expenseType === "operating"
              ? "Operating Expenses"
              : "Direct Expenses",
          )}
        </Tag>
      ),
    },
    {
      title: "Dobavljači",
      key: "suppliers",
      render: (_: any, row: ExpenseCategory) =>
        row.suppliers.length === 0 ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Nema
          </Typography.Text>
        ) : (
          <div className="bc-supplier-table-cell">{renderSupplierList(row)}</div>
        ),
    },
    // {
    //   title: "Vrste rashoda",
    //   key: "subTypes",
    //   render: (_: any, row: ExpenseCategory) => (
    //     <Space size={[4, 4]} wrap>
    //       {row.subTypes.length === 0 && (
    //         <Typography.Text type="secondary" style={{ fontSize: 12 }}>
    //           Nema
    //         </Typography.Text>
    //       )}
    //       {row.subTypes.map((st) => (
    //         <Tag
    //           key={st._id}
    //           className="bc-chip bc-chip--neutral"
    //           onClick={() =>
    //             setEditingSubType({
    //               categoryId: row._id,
    //               subTypeId: st._id,
    //               name: st.name,
    //             })
    //           }
    //         >
    //           {st.name}
    //         </Tag>
    //       ))}
    //     </Space>
    //   ),
    // },
    {
      title: "Status",
      key: "status",
      render: (_: any, row: ExpenseCategory) => (
        <Tag color={row.isFrozen ? "red" : "green"}>
          {row.isFrozen ? t("Frozen") : t("Active")}
        </Tag>
      ),
    },
    {
      title: "Akcije",
      key: "actions",
      render: (_: any, row: ExpenseCategory) => (
        <Space>
          <Button
            size="small"
            type="text"
            className="bc-btn"
            disabled={row.isFrozen}
            onClick={() => {
              setEditingCategory(row);
              setEditCategoryName(row.name);
              setEditCategoryExpenseType(row.expenseType || "operating");
            }}
          >
            Uredi
          </Button>
          <Button
            size="small"
            type="text"
            className="bc-btn"
            disabled={row.isFrozen}
            onClick={() => {
              setSelectedCategory(row);
              setSupplierModalOpen(true);
            }}
          >
            + Dobavljač
          </Button>
          <Button
            size="small"
            type="text"
            className="bc-btn"
            disabled={row.isFrozen}
            onClick={() => {
              setSelectedCategory(row);
              setSubTypeModalOpen(true);
            }}
          >
            + Vrsta
          </Button>
          <Button
            danger={!row.isFrozen}
            type={row.isFrozen ? "primary" : "text"}
            size="small"
            className="bc-btn"
            onClick={() => deleteCategory(row._id, row.isFrozen)}
          >
            {row.isFrozen ? t("Unfreeze") : "Obriši"}
          </Button>
        </Space>
      ),
    },
  ];

  // =======================
  // FETCH CATEGORIES
  // =======================
  const fetchCategories = async () => {
    if (!userData?.clientId) return;

    try {
      const { data } = await axios.get<any>(
        `${BASE_API_URL}/admin/expense-categories`,
        {
          params: {
            clientId: userData.clientId,
            showFrozen: showFrozen ? "true" : "false",
          },
        },
      );

      setCategories(data.categories);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [userData, showFrozen]);

  useEffect(() => {
    if (userData?.clientId) {
      axios
        .get(`${BASE_API_URL}/${userData.clientId}/locations`)
        .then((res) => setLocations((res.data || []).filter(isLocationActive)))
        .catch((err) => console.error(err));
    }
  }, [userData?.clientId]);

  const openEditSupplier = async (categoryId: string, supplierId: string) => {
    try {
      setSupplierLoading(true);
      const { data } = await axios.get(
        `${BASE_API_URL}/admin/expense-categories/${categoryId}/suppliers/${supplierId}`,
      );

      setEditingSupplier({
        categoryId,
        supplierId,
        name: data.name,
        defaultExpense: undefined,
        defaultStartsAt: dayjs(),
        excludeFromBusiness: data.excludeFromBusiness,
        currentDefault: data.currentDefault,
      });
      setSupplierHistory(data.defaultExpenseHistory || []);
      setEditingDefaultLocation("global");
    } catch (error) {
      console.error(error);
      message.error("Ne mogu da učitam dobavljača");
    } finally {
      setSupplierLoading(false);
    }
  };

  // =======================
  // CREATE CATEGORY
  // =======================
  const createCategory = async () => {
    if (!newCategoryName.trim())
      return message.warning("Unesite naziv kategorije");

    try {
      await axios.post(`${BASE_API_URL}/admin/expense-categories`, {
        name: newCategoryName,
        clientId: userData.clientId,
      });

      message.success("Kategorija uspješno dodana");
      setModalOpen(false);
      setNewCategoryName("");
      fetchCategories();
    } catch (error) {
      console.error(error);
    }
  };

  // =======================
  // ADD SUPPLIER
  // =======================
  const addSupplier = async () => {
    if (!newSupplierName.trim())
      return message.warning("Unesite naziv dobavljača");

    if (!selectedCategory) return;

    try {
      await axios.post(
        `${BASE_API_URL}/admin/expense-categories/${selectedCategory._id}/suppliers`,
        {
          name: newSupplierName,
          defaultExpense:
            newSupplierDefault !== null
              ? Math.max(0, Number(newSupplierDefault) || 0)
              : undefined,
          defaultStartsAt: newSupplierStartsAt
            ? newSupplierStartsAt.startOf("day").toISOString()
            : undefined,
          defaultLocationId:
            selectedDefaultLocation && selectedDefaultLocation !== "global"
              ? selectedDefaultLocation
              : undefined,
        },
      );

      message.success("Dobavljač dodan");
      setSupplierModalOpen(false);
      setNewSupplierName("");
      setNewSupplierDefault(null);
      setNewSupplierStartsAt(dayjs());
      fetchCategories();
    } catch (error) {
      console.error(error);
    }
  };

  // =======================
  // ADD SUBTYPE
  // =======================
  const addSubType = async () => {
    if (!newSubTypeName.trim())
      return message.warning("Unesite naziv vrste rashoda");

    if (!selectedCategory) return;

    try {
      await axios.post(
        `${BASE_API_URL}/admin/expense-categories/${selectedCategory._id}/subTypes`,
        { name: newSubTypeName },
      );

      message.success("Vrsta rashoda dodana");
      setSubTypeModalOpen(false);
      setNewSubTypeName("");
      fetchCategories();
    } catch (error) {
      console.error(error);
    }
  };

  // =======================
  // UPDATE CATEGORY NAME
  // =======================
  const updateCategoryName = async () => {
    if (!editingCategory || !editCategoryName.trim()) {
      return message.warning("Unesite naziv kategorije");
    }
    try {
      await axios.put(
        `${BASE_API_URL}/admin/expense-categories/${editingCategory._id}`,
        {
          name: editCategoryName,
          expenseType: editCategoryExpenseType,
        },
      );
      message.success("Kategorija ažurirana");
      setEditingCategory(null);
      setEditCategoryName("");
      setEditCategoryExpenseType("operating");
      fetchCategories();
    } catch (error) {
      console.error(error);
    }
  };

  // =======================
  // UPDATE SUPPLIER
  // =======================
  const updateSupplier = async () => {
    if (!editingSupplier || !editingSupplier.name.trim()) {
      return message.warning("Unesite naziv dobavljača");
    }
    try {
      await axios.put(
        `${BASE_API_URL}/admin/expense-categories/${editingSupplier.categoryId}/suppliers/${editingSupplier.supplierId}`,
        {
          name: editingSupplier.name,
          defaultExpense:
            editingSupplier.defaultExpense !== undefined
              ? Number(editingSupplier.defaultExpense)
              : undefined,
          defaultStartsAt: editingSupplier.defaultStartsAt
            ? editingSupplier.defaultStartsAt
                .startOf?.("day")
                .toISOString?.() ||
              dayjs(editingSupplier.defaultStartsAt)
                .startOf("day")
                .toISOString()
            : undefined,
          defaultLocationId:
            editingDefaultLocation && editingDefaultLocation !== "global"
              ? editingDefaultLocation
              : undefined,
          excludeFromBusiness: editingSupplier.excludeFromBusiness,
        },
      );
      message.success("Dobavljač ažuriran");
      setEditingSupplier(null);
      setSupplierHistory([]);
      setEditingDefaultLocation("global");
      fetchCategories();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteHistory = async (
    categoryId: string,
    supplierId: string,
    history: any,
  ) => {
    if (!history?._id) return;
    const isBaseEntry =
      !history.startsAt &&
      (!history.locationId || history.locationId === "global");
    if (isBaseEntry) {
      message.warning("Osnovni (globalni) zapis ne može biti obrisan.");
      return;
    }
    try {
      await axios.delete(
        `${BASE_API_URL}/admin/expense-categories/${categoryId}/suppliers/${supplierId}/history/${history._id}`,
      );
      message.success("Unos iz istorije obrisan");
      openEditSupplier(categoryId, supplierId);
    } catch (error) {
      console.error(error);
      message.error("Brisanje nije uspelo");
    }
  };

  const handleUpdateHistory = async () => {
    if (!editingSupplier || !editingHistory) return;
    try {
      await axios.put(
        `${BASE_API_URL}/admin/expense-categories/${editingSupplier.categoryId}/suppliers/${editingSupplier.supplierId}/history/${editingHistory.id._id}`,
        {
          value: editingHistory.value,
          startsAt: editingHistory.startsAt
            ? editingHistory.startsAt.startOf("day").toISOString()
            : undefined,
          locationId:
            editingHistory.locationId && editingHistory.locationId !== "global"
              ? editingHistory.locationId
              : null,
        },
      );
      message.success("Istorija ažurirana");
      setEditingHistory(null);
      openEditSupplier(editingSupplier.categoryId, editingSupplier.supplierId);
    } catch (error) {
      console.error(error);
      message.error("Ažuriranje nije uspelo");
    }
  };

  // =======================
  // UPDATE SUBTYPE
  // =======================
  const updateSubType = async () => {
    if (!editingSubType || !editingSubType.name.trim()) {
      return message.warning("Unesite naziv vrste rashoda");
    }
    try {
      await axios.put(
        `${BASE_API_URL}/admin/expense-categories/${editingSubType.categoryId}/subTypes/${editingSubType.subTypeId}`,
        { name: editingSubType.name },
      );
      message.success("Vrsta rashoda ažurirana");
      setEditingSubType(null);
      fetchCategories();
    } catch (error) {
      console.error(error);
    }
  };

  // =======================
  // FREEZE/UNFREEZE SUPPLIER
  // =======================
  const deleteSupplierSafe = async (
    categoryId: string,
    supplierId: string,
    isFrozen?: boolean,
  ) => {
    if (isFrozen) {
      // Unfreeze
      confirm({
        title: t("Unfreeze Supplier?"),
        content: t("The supplier will be available again."),
        okText: t("Unfreeze"),
        onOk: async () => {
          try {
            await axios.post(
              `${BASE_API_URL}/admin/expense-categories/${categoryId}/suppliers/${supplierId}/unfreeze`,
            );
            message.success(t("Supplier unfrozen"));
            fetchCategories();
          } catch (error) {
            console.error(error);
            message.error(t("Error unfreezing supplier"));
          }
        },
      });
    } else {
      // Freeze
      confirm({
        title: t("Freeze Supplier?"),
        content: t(
          "The supplier will not be visible in lists, but data remains saved.",
        ),
        okText: t("Freeze"),
        okType: "danger",
        onOk: async () => {
          try {
            await axios.delete(
              `${BASE_API_URL}/admin/expense-categories/${categoryId}/suppliers/${supplierId}`,
              {
                data: { userId: userData.id },
              },
            );
            message.success(t("Supplier frozen"));
            fetchCategories();
          } catch (error) {
            console.error(error);
            message.error(t("Error freezing supplier"));
          }
        },
      });
    }
  };

  // =======================
  // PERMANENTLY DELETE SUPPLIER (Critical Operation)
  // =======================
  const permanentlyDeleteSupplier = async (
    categoryId: string,
    supplierId: string,
    supplierName: string,
  ) => {
    let confirmationInput = "";

    Modal.confirm({
      title: (
        <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
          ⚠️ {t("Permanently Delete Supplier")}
        </span>
      ),
      width: 600,
      okText: t("Delete Permanently"),
      okType: "danger",
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      content: (
        <div style={{ marginTop: 16 }}>
          <Typography.Paragraph strong style={{ color: "#ff4d4f" }}>
            {t("WARNING: This action is IRREVERSIBLE!")}
          </Typography.Paragraph>
          <Typography.Paragraph>
            {t("You are about to permanently delete supplier:")}{" "}
            <strong>{supplierName}</strong>
          </Typography.Paragraph>
          <Typography.Paragraph>
            {t(
              "This will remove ALL data including history. This operation cannot be undone.",
            )}
          </Typography.Paragraph>
          <Typography.Paragraph strong>
            {t("Type")} <code>PERMANENTLY DELETE</code> {t("to confirm")}:
          </Typography.Paragraph>
          <Input
            placeholder="PERMANENTLY DELETE"
            onChange={(e) => (confirmationInput = e.target.value)}
            style={{ marginTop: 8 }}
          />
        </div>
      ),
      onOk: async () => {
        if (confirmationInput !== "PERMANENTLY DELETE") {
          message.error(t("Confirmation text does not match"));
          return Promise.reject();
        }

        try {
          await axios.post(
            `${BASE_API_URL}/admin/expense-categories/${categoryId}/suppliers/${supplierId}/permanently-delete`,
            {
              confirmationText: confirmationInput,
              userId: userData.id,
            },
          );
          message.success(t("Supplier permanently deleted"));
          fetchCategories();
        } catch (error: any) {
          console.error(error);
          message.error(
            error?.response?.data?.message ||
              t("Error permanently deleting supplier"),
          );
          return Promise.reject();
        }
      },
    });
  };

  // =======================
  // FREEZE/UNFREEZE CATEGORY
  // =======================
  const deleteCategory = (id: string, isFrozen?: boolean) => {
    if (isFrozen) {
      // Unfreeze
      confirm({
        title: "Odmrznuti kategoriju?",
        content: "Kategorija će ponovo biti dostupna.",
        okText: "Odmrzni",
        onOk: async () => {
          try {
            await axios.post(
              `${BASE_API_URL}/admin/expense-categories/${id}/unfreeze`,
            );
            message.success("Kategorija odmrznuta");
            fetchCategories();
          } catch (error) {
            console.error(error);
            message.error("Greška pri odmrzavanju kategorije");
          }
        },
      });
    } else {
      // Freeze
      confirm({
        title: "Zamrznuti kategoriju?",
        content:
          "Kategorija neće biti vidljiva u listama, ali podaci ostaju sačuvani.",
        okText: "Zamrzni",
        okType: "danger",
        onOk: async () => {
          try {
            await axios.delete(
              `${BASE_API_URL}/admin/expense-categories/${id}`,
              {
                data: { userId: userData.id },
              },
            );
            message.success("Kategorija zamrznuta");
            fetchCategories();
          } catch (error) {
            console.error(error);
            message.error("Greška pri zamrzavanju kategorije");
          }
        },
      });
    }
  };

  // =======================
  // RENDER
  // =======================
  return (
    <div className="bc-settings">
      <Card className="bc-hero">
        <div className="bc-hero__content">
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Poslovne kategorije i prihodi
            </Typography.Title>
            <Typography.Text type="secondary">
              Uredi kategorije troškova, dobavljače, podtipove i tipove dodatnih
              prihoda.
            </Typography.Text>
          </div>
        </div>
      </Card>

      <Tabs defaultActiveKey="1" className="bc-tabs">
        <Tabs.TabPane tab="Kategorije troškova" key="1">
          <Card className="bc-hero" style={{ marginTop: 16 }}>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Button
                size="small"
                className="bc-btn"
                type={viewMode === "cards" ? "primary" : "default"}
                onClick={() => setViewMode("cards")}
              >
                Grid
              </Button>
              <Button
                size="small"
                className="bc-btn"
                type={viewMode === "table" ? "primary" : "default"}
                onClick={() => setViewMode("table")}
              >
                Tabela
              </Button>
              <Input
                allowClear
                placeholder="Filtriraj kategorije"
                value={filterTerm}
                onChange={(e) => setFilterTerm(e.target.value)}
                style={{ width: 200 }}
              />
              <Button
                size="small"
                className="bc-btn"
                icon={
                  supplierSort === "asc" ? (
                    <SortAscendingOutlined />
                  ) : (
                    <SortDescendingOutlined />
                  )
                }
                onClick={() =>
                  setSupplierSort((d) => (d === "asc" ? "desc" : "asc"))
                }
                title={t("Sort suppliers")}
              >
                {supplierSort === "asc" ? "A → Z" : "Z → A"}
              </Button>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Switch
                  checked={showFrozen}
                  onChange={(value) => setShowFrozen(value)}
                  size="small"
                />
                <span style={{ fontSize: 14 }}>{t("Show frozen")}</span>
              </div>
              <Button
                type="primary"
                className="bc-btn bc-btn--primary"
                onClick={() => setModalOpen(true)}
              >
                Dodaj kategoriju
              </Button>
            </div>
            <div className="bc-hero__stats">
              <div>
                <div className="bc-hero__stat-value">
                  {categories?.length || 0}
                </div>
                <div className="bc-hero__stat-label">Kategorija</div>
              </div>
              <Divider type="vertical" />
              <div>
                <div className="bc-hero__stat-value">
                  {categories?.reduce(
                    (acc: number, c: ExpenseCategory) =>
                      acc + (c.suppliers?.length || 0),
                    0,
                  ) || 0}
                </div>
                <div className="bc-hero__stat-label">Dobavljača</div>
              </div>
              <Divider type="vertical" />
              <div>
                <div className="bc-hero__stat-value">
                  {categories?.reduce(
                    (acc: number, c: ExpenseCategory) =>
                      acc + (c.subTypes?.length || 0),
                    0,
                  ) || 0}
                </div>
                <div className="bc-hero__stat-label">Podtipova</div>
              </div>
            </div>
          </Card>

          {viewMode === "cards" && (
            <Row gutter={[16, 16]} className="bc-grid">
              {filteredCategories.length === 0 && (
                <Col span={24}>
                  <Card className="bc-card" bordered={false}>
                    <Typography.Text type="secondary">
                      Nema kategorija za prikaz.
                    </Typography.Text>
                  </Card>
                </Col>
              )}
              {filteredCategories.map((cat: ExpenseCategory) => (
                <Col xs={24} md={12} lg={8} key={cat._id}>
                  <Card className="bc-card" bordered={false}>
                    <div className="bc-card__header">
                      <div>
                        <Typography.Text strong style={{ fontSize: 16 }}>
                          {cat.name}
                        </Typography.Text>
                        <div className="bc-card__meta">
                          {cat.suppliers.length} dobavljača ·{" "}
                          {cat.subTypes.length} podtipova
                        </div>
                        {cat.expenseType && (
                          <div style={{ marginTop: 6 }}>
                            <Tag
                              color={
                                cat.expenseType === "operating"
                                  ? "blue"
                                  : "green"
                              }
                              style={{ fontSize: 11 }}
                            >
                              {t(
                                cat.expenseType === "operating"
                                  ? "Operating Expenses"
                                  : "Direct Expenses",
                              )}
                            </Tag>
                            {cat.isFrozen && (
                              <Tag
                                color="red"
                                style={{ fontSize: 11, marginLeft: 4 }}
                              >
                                {t("Frozen")}
                              </Tag>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="bc-card__actions">
                        <Button
                          size="small"
                          type="text"
                          className="bc-btn"
                          onClick={() => {
                            setEditingCategory(cat);
                            setEditCategoryName(cat.name);
                            setEditCategoryExpenseType(
                              cat.expenseType || "operating",
                            );
                          }}
                          disabled={cat.isFrozen}
                        >
                          Uredi
                        </Button>
                        <Button
                          danger={!cat.isFrozen}
                          type={cat.isFrozen ? "primary" : "text"}
                          size="small"
                          className="bc-btn"
                          onClick={() => deleteCategory(cat._id, cat.isFrozen)}
                        >
                          {cat.isFrozen ? t("Unfreeze") : t("Freeze")}
                        </Button>
                      </div>
                    </div>

                    <div className="bc-card__section">
                      <div className="bc-card__section-title">
                        <span>Dobavljači</span>
                        <Button
                          size="small"
                          type="text"
                          className="bc-btn"
                          onClick={() => {
                            setSelectedCategory(cat);
                            setSupplierModalOpen(true);
                          }}
                        >
                          + Dodaj dobavljača
                        </Button>
                      </div>
                      {cat.suppliers.length === 0 ? (
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          Nema dodanih dobavljača
                        </Typography.Text>
                      ) : (
                        renderSupplierList(cat)
                      )}
                      <Typography.Text type="secondary" className="bc-hint">
                        {t("Click a supplier to edit. Use the lock icon to freeze/unfreeze.")}
                      </Typography.Text>
                    </div>

                    <div className="bc-card__section">
                      {/* <div className="bc-card__section-title">
                  <span>Vrste rashoda</span>
                    <Button
                      size="small"
                      type="text"
                      className="bc-btn"
                      onClick={() => {
                        setSelectedCategory(cat);
                        setSubTypeModalOpen(true);
                      }}
                    >
                      + Dodaj vrstu
                    </Button>
                  </div> */}
                      <div className="bc-chip-row">
                        {cat.subTypes.length === 0 && (
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                          >
                            Nema dodatih vrsta
                          </Typography.Text>
                        )}
                        {cat.subTypes.map((st) => (
                          <Tag
                            key={st._id}
                            className="bc-chip bc-chip--neutral"
                            onClick={() =>
                              setEditingSubType({
                                categoryId: cat._id,
                                subTypeId: st._id,
                                name: st.name,
                              })
                            }
                          >
                            {st.name}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {viewMode === "table" && (
            <Card className="bc-card" bordered={false}>
              <Typography.Text
                type="secondary"
                className="bc-hint"
                style={{ marginBottom: 8, display: "block" }}
              >
                Klik na tag da urediš, X na dobavljaču briše.
              </Typography.Text>
              <Table
                columns={tableColumns as any}
                dataSource={filteredCategories}
                rowKey="_id"
                pagination={false}
                size="middle"
                className="bc-table"
              />
            </Card>
          )}

          {/* CATEGORY MODAL */}
          <Modal
            title="Nova kategorija"
            open={isModalOpen}
            className="standardHeader standardFooter"
            onCancel={() => setModalOpen(false)}
            onOk={createCategory}
            okText="Sačuvaj"
          >
            <Input
              placeholder="Naziv kategorije"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
          </Modal>

          {/* EDIT CATEGORY */}
          <Modal
            title="Uredi kategoriju"
            open={!!editingCategory}
            className="standardHeader standardFooter"
            onCancel={() => {
              setEditingCategory(null);
              setEditCategoryName("");
              setEditCategoryExpenseType("operating");
            }}
            onOk={updateCategoryName}
            okText="Sačuvaj"
          >
            <Input
              placeholder="Naziv kategorije"
              value={editCategoryName}
              onChange={(e) => setEditCategoryName(e.target.value)}
              style={{ marginBottom: 16 }}
            />
            <div style={{ marginBottom: 8 }}>
              <Typography.Text strong>{t("Expense Type")}</Typography.Text>
            </div>
            <Select
              value={editCategoryExpenseType}
              onChange={(value) => setEditCategoryExpenseType(value)}
              style={{ width: "100%" }}
            >
              <Option value="operating">{t("Operating Expenses")}</Option>
              <Option value="direct">{t("Direct Expenses")}</Option>
            </Select>
          </Modal>

          {/* SUPPLIER MODAL */}
          <Modal
            title={`Dodaj dobavljača u: ${selectedCategory?.name || ""}`}
            open={isSupplierModalOpen}
            className="standardHeader standardFooter"
            onCancel={() => {
              setSupplierModalOpen(false);
              setNewSupplierName("");
              setNewSupplierDefault(0);
              setNewSupplierStartsAt(dayjs());
              setSelectedDefaultLocation("global");
            }}
            onOk={addSupplier}
            okText="Sačuvaj"
          >
            <Input
              placeholder="Naziv dobavljača"
              value={newSupplierName}
              onChange={(e) => setNewSupplierName(e.target.value)}
            />
            <InputNumber
              placeholder="Default vrednost (opciono)"
              style={{ width: "100%", marginTop: 8 }}
              min={0}
              value={(newSupplierDefault as number | null) ?? 0}
              onChange={(v) =>
                setNewSupplierDefault(
                  v === null || v === undefined ? 0 : Math.max(0, Number(v)),
                )
              }
            />
            <DatePicker
              style={{ width: "100%", marginTop: 8 }}
              value={newSupplierStartsAt || dayjs()}
              onChange={(v) => setNewSupplierStartsAt(v)}
              placeholder="Datum početka (default danas)"
            />
            <Select
              style={{ width: "100%", marginTop: 8 }}
              value={selectedDefaultLocation || "global"}
              onChange={(v) => setSelectedDefaultLocation(v)}
              placeholder="Lokacija (opciono)"
            >
              <Select.Option value="global">Globalno</Select.Option>
              {(locations || []).map((loc) => (
                <Select.Option key={loc._id} value={loc._id}>
                  {loc.name}
                </Select.Option>
              ))}
            </Select>
          </Modal>

          {/* EDIT SUPPLIER */}
          <Modal
            title="Uredi dobavljača"
            open={!!editingSupplier}
            className="standardHeader standardFooter"
            onCancel={() => {
              setEditingSupplier(null);
              setSupplierHistory([]);
              setEditingDefaultLocation("global");
            }}
            onOk={updateSupplier}
            okText="Sačuvaj"
            confirmLoading={supplierLoading}
            footer={[
              <Button
                key="delete"
                danger
                type="text"
                style={{ float: "left" }}
                onClick={() => {
                  if (editingSupplier) {
                    permanentlyDeleteSupplier(
                      editingSupplier.categoryId,
                      editingSupplier.supplierId,
                      editingSupplier.name,
                    );
                    setEditingSupplier(null);
                    setSupplierHistory([]);
                  }
                }}
              >
                🗑️ {t("Delete Permanently")}
              </Button>,
              <Button
                key="cancel"
                onClick={() => {
                  setEditingSupplier(null);
                  setSupplierHistory([]);
                  setEditingDefaultLocation("global");
                }}
              >
                {t("Cancel")}
              </Button>,
              <Button
                key="submit"
                type="primary"
                loading={supplierLoading}
                onClick={updateSupplier}
              >
                {t("Save")}
              </Button>,
            ]}
          >
            <Input
              placeholder="Naziv dobavljača"
              value={editingSupplier?.name || ""}
              onChange={(e) =>
                setEditingSupplier((prev) =>
                  prev
                    ? { ...prev, name: e.target.value }
                    : {
                        categoryId: "",
                        supplierId: "",
                        name: e.target.value,
                      },
                )
              }
            />
            <InputNumber
              placeholder="Novi default (opciono)"
              style={{ width: "100%", marginTop: 8 }}
              value={editingSupplier?.defaultExpense}
              onChange={(v) =>
                setEditingSupplier((prev) =>
                  prev ? { ...prev, defaultExpense: v ?? undefined } : prev,
                )
              }
            />
            <DatePicker
              style={{ width: "100%", marginTop: 8 }}
              value={editingSupplier?.defaultStartsAt || dayjs()}
              onChange={(v) =>
                setEditingSupplier((prev) =>
                  prev ? { ...prev, defaultStartsAt: v } : prev,
                )
              }
              placeholder="Datum početka važenja"
            />
            <Select
              style={{ width: "100%", marginTop: 8 }}
              value={editingDefaultLocation || "global"}
              onChange={(v) => setEditingDefaultLocation(v)}
              placeholder="Lokacija (opciono)"
            >
              <Select.Option value="global">Globalno</Select.Option>
              {(locations || []).map((loc) => (
                <Select.Option key={loc._id} value={loc._id}>
                  {loc.name}
                </Select.Option>
              ))}
            </Select>
            <div style={{ marginTop: 8 }}>
              <Checkbox
                checked={!!editingSupplier?.excludeFromBusiness}
                onChange={(e) =>
                  setEditingSupplier((prev) =>
                    prev
                      ? { ...prev, excludeFromBusiness: e.target.checked }
                      : prev,
                  )
                }
              >
                Ne uračunavaj u poslovanje (Other)
              </Checkbox>
            </div>
            <Divider />
            <Typography.Text strong>Istorija default troškova</Typography.Text>
            <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
              {supplierHistory.length === 0 && (
                <Typography.Text type="secondary">
                  Nema istorije za prikaz.
                </Typography.Text>
              )}
              {supplierHistory.map((h, idx) => {
                const isBaseHistory =
                  !h.startsAt &&
                  (!h.locationId ||
                    h.locationId === "global" ||
                    h.locationId === null);

                return (
                  <div
                    key={`${h.startsAt}-${idx}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 10px",
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <span>
                        {dayjs(h.startsAt).format("DD.MM.YYYY")} ·{" "}
                        {h.locationId && locationMap[h.locationId]
                          ? locationMap[h.locationId]
                          : "Globalno"}
                      </span>
                      <div style={{ fontWeight: 600 }}>{h.value} RSD</div>
                    </div>
                    <Space>
                      <Button
                        size="small"
                        disabled={isBaseHistory}
                        onClick={() =>
                          setEditingHistory({
                            id: h as any,
                            value: h.value,
                            startsAt: dayjs(h.startsAt),
                            locationId: h.locationId || "global",
                          })
                        }
                      >
                        Uredi
                      </Button>
                      <Button
                        size="small"
                        danger
                        disabled={isBaseHistory}
                        onClick={() =>
                          handleDeleteHistory(
                            editingSupplier?.categoryId || "",
                            editingSupplier?.supplierId || "",
                            h as any,
                          )
                        }
                      >
                        Obriši
                      </Button>
                    </Space>
                  </div>
                );
              })}
            </div>
          </Modal>

          {/* EDIT HISTORY ENTRY */}
          <Modal
            title="Uredi istoriju"
            open={!!editingHistory}
            className="standardHeader standardFooter"
            onCancel={() => setEditingHistory(null)}
            onOk={handleUpdateHistory}
            okText="Sačuvaj"
          >
            <InputNumber
              style={{ width: "100%", marginBottom: 8 }}
              placeholder="Iznos"
              value={editingHistory?.value}
              onChange={(v) =>
                setEditingHistory((prev) =>
                  prev ? { ...prev, value: v ?? 0 } : prev,
                )
              }
            />
            <DatePicker
              style={{ width: "100%", marginBottom: 8 }}
              value={editingHistory?.startsAt || dayjs()}
              onChange={(v) =>
                setEditingHistory((prev) =>
                  prev ? { ...prev, startsAt: v || dayjs() } : prev,
                )
              }
            />
            <Select
              style={{ width: "100%" }}
              value={editingHistory?.locationId || "global"}
              onChange={(v) =>
                setEditingHistory((prev) =>
                  prev ? { ...prev, locationId: v as any } : prev,
                )
              }
            >
              <Option value="global">Globalno</Option>
              {locations.map((loc) => (
                <Option key={loc._id} value={loc._id}>
                  {loc.name}
                </Option>
              ))}
            </Select>
          </Modal>

          {/* SUBTYPE MODAL */}
          <Modal
            title={`Dodaj vrstu rashoda u: ${selectedCategory?.name || ""}`}
            open={isSubTypeModalOpen}
            className="standardHeader standardFooter"
            onCancel={() => setSubTypeModalOpen(false)}
            onOk={addSubType}
            okText="Sačuvaj"
          >
            <Input
              placeholder="Naziv vrste rashoda"
              value={newSubTypeName}
              onChange={(e) => setNewSubTypeName(e.target.value)}
            />
          </Modal>

          {/* EDIT SUBTYPE */}
          <Modal
            title="Uredi vrstu rashoda"
            open={!!editingSubType}
            className="standardHeader standardFooter"
            onCancel={() => setEditingSubType(null)}
            onOk={updateSubType}
            okText="Sačuvaj"
          >
            <Input
              placeholder="Naziv vrste rashoda"
              value={editingSubType?.name || ""}
              onChange={(e) =>
                setEditingSubType((prev) =>
                  prev
                    ? { ...prev, name: e.target.value }
                    : {
                        categoryId: "",
                        subTypeId: "",
                        name: e.target.value,
                      },
                )
              }
            />
          </Modal>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Tipovi dodatnih prihoda" key="2">
          <ExtraIncomeTypesTab />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default BusinessCategoriesSettings;
